import React from "react";
import { ReviewTileItem } from "@/types/tile.types";
import type {
  RiskPoint,
  Ambiguity,
  ConflictingTerm,
  ComplexLanguage,
  NegotiationPoint,
  NonStandardClause,
  UndefinedTerm,
  Placeholder,
  CrossReferencingClause,
  SequentialOrder,
  MissingClause,
  Inconsistency,
  CustomReview,
  TileTypes,
} from "@/types/review.types";

const TileField = ({ label, value }: { label: string; value?: React.ReactNode }) => {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div className="text-sm">
      <p className="text-neutral-400 whitespace-pre-wrap">
        <strong>{label}:&nbsp;</strong>
        {value}
      </p>
    </div>
  );
};

export const useFormatTile = () => {
  const formatTileContent = (tile: ReviewTileItem) => {
    const item = tile.originalData;
    const tileType = tile.originalKey;

    if (item === undefined) return null;

    const content: React.ReactNode[] = [];

    if ("documentName" in item && item.documentName !== undefined) {
      content.push(<TileField key="docName" label="Document Name" value={item.documentName} />);
    }

    switch (tileType) {
      case "riskPoints":
        return formatRiskPoints(item as RiskPoint, content);
      case "ambiguities":
        return formatAmbiguities(item as Ambiguity, content);
      case "conflictingTerms":
        return formatConflictingTerms(item as ConflictingTerm, content);
      case "complexLanguage":
        return formatComplexLanguage(item as ComplexLanguage, content);
      case "negotiationPoints":
        return formatNegotiationPoints(item as NegotiationPoint, content);
      case "nonStandardClauses":
        return formatNonStandardClauses(item as NonStandardClause, content);
      case "undefinedTerms":
        return formatUndefinedTerms(item as UndefinedTerm, content);
      case "placeholders":
        return formatPlaceholders(item as Placeholder, content);
      case "crossReferencingClauses":
        return formatCrossReferencingClauses(item as CrossReferencingClause, content);
      case "sequentialOrder":
        return formatSequentialOrder(item as SequentialOrder, content);
      case "missingClauses":
        return formatMissingClauses(item as MissingClause, content);
      case "inconsistencies":
        return formatInconsistencies(item as Inconsistency, content);
      case "customReview":
        return formatCustomReview(item as CustomReview, content);
      // additionalClauses and comparisons are not handled — compare precedent
      // is hidden from the generate review form so these types are never returned.
      default:
        return <p className="text-sm">{getFallbackText(item)}</p>;
    }
  };

  return { formatTileContent };
};

function getFallbackText(item: TileTypes): string {
  if ("reason" in item && item.reason !== undefined) return item.reason;
  if ("analysis" in item && item.analysis !== undefined) return item.analysis;
  if ("explanation" in item && item.explanation !== undefined) return item.explanation;
  return "No content available";
}

function formatRiskPoints(item: RiskPoint, content: React.ReactNode[]) {
  content.push(<TileField key="clause" label="Clause" value={item.clause} />);
  content.push(<TileField key="riskText" label="Risk Text" value={item.riskText} />);
  content.push(
    <TileField key="riskExplanation" label="Risk Explanation" value={item.riskExplanation} />
  );
  content.push(<TileField key="suggestions" label="Suggestion" value={item.suggestions} />);
  return <>{content}</>;
}

function formatAmbiguities(item: Ambiguity, content: React.ReactNode[]) {
  content.push(<TileField key="clause" label="Clause" value={item.clause} />);
  content.push(<TileField key="ambiguousText" label="Ambiguous Text" value={item.ambiguousText} />);
  content.push(<TileField key="reason" label="Reason" value={item.reason} />);
  content.push(<TileField key="suggestedclause" label="Suggestion" value={item.suggestedclause} />);
  return <>{content}</>;
}

function formatConflictingTerms(item: ConflictingTerm, content: React.ReactNode[]) {
  content.push(<TileField key="clause1" label="Clause Number" value={item.clause1?.number} />);
  content.push(<TileField key="clause2" label="Clause Number" value={item.clause2?.number} />);
  content.push(
    <TileField key="relevant_text" label="Relevant Text" value={item.clause1?.relevant_text} />
  );
  content.push(<TileField key="reason" label="Reason" value={item.reason} />);
  content.push(<TileField key="suggestedclause" label="Suggestion" value={item.suggestedclause} />);
  return <>{content}</>;
}

function formatComplexLanguage(item: ComplexLanguage, content: React.ReactNode[]) {
  content.push(<TileField key="clause" label="Clause" value={item.clause} />);
  content.push(<TileField key="complexText" label="Complex Text" value={item.complexText} />);
  content.push(<TileField key="reason" label="Reason" value={item.reason} />);
  content.push(<TileField key="suggestions" label="Suggestions" value={item.suggestions} />);
  return <>{content}</>;
}

function formatNegotiationPoints(item: NegotiationPoint, content: React.ReactNode[]) {
  if (item.email !== undefined) {
    content.push(<TileField key="email" label="Email" value={item.email} />);
  } else {
    content.push(<TileField key="clause" label="Clause" value={item.clause} />);
    content.push(
      <TileField key="negotiationText" label="Negotiation Text" value={item.negotiationText} />
    );
    content.push(<TileField key="points" label="Points" value={item.points} />);
    content.push(<TileField key="suggestion" label="Suggestion" value={item.suggestion} />);
  }
  return <>{content}</>;
}

function formatNonStandardClauses(item: NonStandardClause, content: React.ReactNode[]) {
  content.push(<TileField key="clause" label="Clause" value={item.clause} />);
  content.push(
    <TileField key="nonStandardText" label="Non-Standard Text" value={item.nonStandardText} />
  );
  content.push(<TileField key="reason" label="Reason" value={item.reason} />);
  content.push(<TileField key="suggestedclause" label="Suggestion" value={item.suggestedclause} />);
  return <>{content}</>;
}

function formatUndefinedTerms(item: UndefinedTerm, content: React.ReactNode[]) {
  content.push(<TileField key="contextText" label="Context" value={item.contextText} />);
  content.push(<TileField key="reason" label="Reason" value={item.reason} />);
  content.push(<TileField key="suggestedclause" label="Suggestion" value={item.suggestedclause} />);
  return <>{content}</>;
}

function formatPlaceholders(item: Placeholder, content: React.ReactNode[]) {
  content.push(<TileField key="placeholder" label="Placeholder" value={item.placeholder} />);
  return <>{content}</>;
}

function formatCrossReferencingClauses(item: CrossReferencingClause, content: React.ReactNode[]) {
  content.push(<TileField key="clause" label="Clause" value={item.clause} />);
  content.push(<TileField key="context" label="Context" value={item.context} />);
  content.push(<TileField key="suggestion" label="Suggestion" value={item.suggestion} />);
  return <>{content}</>;
}

function formatSequentialOrder(item: SequentialOrder, content: React.ReactNode[]) {
  content.push(<TileField key="currentNumber" label="Current Number" value={item.currentNumber} />);
  content.push(<TileField key="context" label="Context" value={item.context} />);
  content.push(
    <TileField key="suggestedNumber" label="Suggested Number" value={item.suggestedNumber} />
  );
  content.push(<TileField key="reason" label="Reason" value={item.reason} />);
  return <>{content}</>;
}

function formatMissingClauses(item: MissingClause, content: React.ReactNode[]) {
  content.push(<TileField key="clause" label="Clause" value={item.clause} />);
  content.push(<TileField key="reason" label="Reason" value={item.reason} />);
  content.push(<TileField key="suggestedclause" label="Suggestion" value={item.suggestedclause} />);
  content.push(
    <TileField key="insertionPoint" label="Insertion Point Under" value={item.insertionPoint} />
  );
  return <>{content}</>;
}

function formatInconsistencies(item: Inconsistency, content: React.ReactNode[]) {
  if (item.document1 !== undefined && item.document2 !== undefined) {
    content.push(<TileField key="doc1" label="Document 1" value={item.document1} />);
    content.push(<TileField key="doc2" label="Document 2" value={item.document2} />);
  }
  content.push(<TileField key="text1" label="Text 1" value={item.text1} />);
  content.push(<TileField key="text2" label="Text 2" value={item.text2} />);
  content.push(<TileField key="explanation" label="Explanation" value={item.explanation} />);
  content.push(<TileField key="suggestion" label="Suggestion" value={item.suggestion} />);
  return <>{content}</>;
}

function formatCustomReview(item: CustomReview, content: React.ReactNode[]) {
  content.push(<TileField key="clause" label="Clause" value={item.clause} />);
  content.push(<TileField key="relevantText" label="Relevant Text" value={item.relevantText} />);
  content.push(<TileField key="reason" label="Reason" value={item.reason} />);
  content.push(<TileField key="suggestion" label="Suggestion" value={item.suggestion} />);
  return <>{content}</>;
}
